import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import Layout from "@/components/Layout";
import InstallPrompt from "@/components/InstallPrompt";
import Dashboard from "@/pages/Dashboard";
import Agenda from "@/pages/Agenda";
import QuickRepairs from "@/pages/QuickRepairs";
import Mechanics from "@/pages/Mechanics";
import SettingsPage from "@/pages/Settings";
import Vehicles from "@/pages/Vehicles";
import Welcome from "@/pages/Welcome";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import Subscribe from "@/pages/Subscribe";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Landing from "@/pages/Landing";
import AvisoLegal from "@/pages/legal/AvisoLegal";
import Privacidad from "@/pages/legal/Privacidad";
import CookiesPage from "@/pages/legal/Cookies";
import Terminos from "@/pages/legal/Terminos";
import Contratacion from "@/pages/legal/Contratacion";
import CookieBanner from "@/components/CookieBanner";
import DeleteAccount from "@/pages/DeleteAccount";
import { AuthProvider, useAuth } from "@/context/AuthContext";

function GoogleCallbackWatcher() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return null;
}

function Protected({ children }) {
  const { workshop } = useAuth();
  const location = useLocation();
  if (workshop === null) return null; // still checking session
  if (!workshop) {
    return <Navigate to={`/entrar?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return children;
}

function WelcomeGate() {
  const { workshop } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (workshop) {
      const params = new URLSearchParams(location.search);
      const next = params.get("next") || "/panel";
      navigate(next, { replace: true });
    }
  }, [workshop, navigate, location.search]);
  if (workshop) return null;
  return <Welcome />;
}

function AppRoutes() {
  const location = useLocation();
  // Handle Google OAuth callback regardless of route
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      {/* Public marketing */}
      <Route path="/" element={<Landing />} />
      <Route path="/entrar" element={<WelcomeGate />} />

      {/* Legal (public) */}
      <Route path="/aviso-legal" element={<AvisoLegal />} />
      <Route path="/privacidad" element={<Privacidad />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/terminos" element={<Terminos />} />
      <Route path="/contratacion" element={<Contratacion />} />
      <Route path="/eliminar-cuenta" element={<DeleteAccount />} />

      {/* Admin (separate access) */}
      <Route path="/admin" element={<Admin />} />

      {/* Protected app under /panel + sub-routes */}
      <Route path="/panel" element={<Protected><LayoutWithChildren><Dashboard /></LayoutWithChildren></Protected>} />
      <Route path="/agenda" element={<Protected><LayoutWithChildren><Agenda /></LayoutWithChildren></Protected>} />
      <Route path="/rapida" element={<Protected><LayoutWithChildren><QuickRepairs /></LayoutWithChildren></Protected>} />
      <Route path="/vehiculos" element={<Protected><LayoutWithChildren><Vehicles /></LayoutWithChildren></Protected>} />
      <Route path="/mecanicos" element={<Protected><LayoutWithChildren><Mechanics /></LayoutWithChildren></Protected>} />
      <Route path="/ajustes" element={<Protected><LayoutWithChildren><SettingsPage /></LayoutWithChildren></Protected>} />
      <Route path="/suscripcion" element={<Protected><LayoutWithChildren><Subscribe /></LayoutWithChildren></Protected>} />
      <Route path="/payment/success" element={<Protected><LayoutWithChildren><PaymentSuccess /></LayoutWithChildren></Protected>} />
      <Route path="/payment/cancel" element={<Protected><LayoutWithChildren><PaymentCancel /></LayoutWithChildren></Protected>} />

      {/* Fallback → landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function LayoutWithChildren({ children }) {
  const { workshop, logout } = useAuth();
  return (
    <Layout workshop={workshop} onLogout={logout}>
      {children}
    </Layout>
  );
}

function PWAInstallPromptGate() {
  const location = useLocation();
  // Show install prompt only inside the authenticated app, not on marketing/landing/login
  const inApp = location.pathname.startsWith("/panel") ||
    location.pathname.startsWith("/agenda") ||
    location.pathname.startsWith("/rapida") ||
    location.pathname.startsWith("/vehiculos") ||
    location.pathname.startsWith("/mecanicos") ||
    location.pathname.startsWith("/ajustes") ||
    location.pathname.startsWith("/suscripcion");
  if (!inApp) return null;
  return <InstallPrompt />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <GoogleCallbackWatcher />
          <PWAInstallPromptGate />
          <CookieBanner />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
